import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import { Check, X, Play, Shield, Star, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(e.target); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

function Reveal({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  const { ref, visible } = useReveal(0.1);
  return (
    <div ref={ref} className={className} style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: `all 0.6s cubic-bezier(.22,1,.36,1) ${delay}ms` }}>
      {children}
    </div>
  );
}

const toolkitFeatures = [
  "AI Content Monetisation Playbook",
  "27 AI Prompts",
  "90-Day Content Calendar",
  "Pricing Calculator",
  "Sales Page Templates",
  "Launch Email Swipe File",
];

const fullSystemFeatures = [
  ...toolkitFeatures,
  "Full video training course",
  "Private community access",
  "Monthly live Q&A calls",
  "AI automation workflows",
  "Sales funnel templates",
  "Email marketing sequences",
];

const testimonials = [
  { name: "Jake", role: "Finance YouTuber, 12K subs", text: "The full system helped me launch my first course and make £2,300 in the first month. The community alone is worth 10x the price." },
  { name: "Priya", role: "Wellness Instagram, 18K followers", text: "I went from zero products to £4K/month recurring revenue in 8 weeks. The AI workflows changed everything." },
];

export default function Upgrade() {
  const [searchParams] = useSearchParams();
  const [purchasing, setPurchasing] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(t);
  }, []);

  const handleUpgrade = async () => {
    setPurchasing(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-oto-checkout", {
        body: { email: searchParams.get("email") || "" },
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch (err: any) {
      toast.error("Something went wrong. Please try again.");
      setPurchasing(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FFFFFF" }}>
      <Helmet>
        <title>Upgrade to Full System | AI StoryCraft Studio</title>
        <meta name="description" content="Upgrade to the complete AI StoryCraft Studio system — full course, community, and implementation system at an exclusive price." />
      </Helmet>

      {/* Confetti placeholder */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50 flex items-start justify-center pt-8">
          <div className="flex gap-2">
            {["🎉", "🎊", "✨", "🎉", "🎊"].map((e, i) => (
              <span key={i} className="text-3xl animate-bounce" style={{ animationDelay: `${i * 150}ms` }}>{e}</span>
            ))}
          </div>
        </div>
      )}

      <div className="mx-auto px-4 md:px-6" style={{ maxWidth: 820 }}>
        {/* Success Header */}
        <Reveal>
          <div className="text-center pt-12 md:pt-16 pb-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ backgroundColor: "#ECFDF5", color: "#065F46" }}>
              <Check className="w-4 h-4" />
              <span className="text-sm font-semibold">Your Toolkit Purchase Was Successful! 🎉</span>
            </div>
            <p className="text-sm mb-8" style={{ color: "#6B7280" }}>Your AI Creator Monetisation Toolkit is ready to download. But before you access it...</p>
          </div>
        </Reveal>

        {/* Main Offer */}
        <Reveal delay={100}>
          <div className="text-center pb-8">
            <h1 className="text-2xl md:text-[2.5rem] font-extrabold leading-tight mb-4" style={{ color: "#1F2937" }}>
              Want the <span style={{ color: "#F59E0B" }}>Complete System</span> Instead of Just the Toolkit?
            </h1>
            <p className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto" style={{ color: "#6B7280" }}>
              Get AI StoryCraft Studio — the full course, community, and implementation system — at an exclusive price you'll never see again.
            </p>
          </div>
        </Reveal>

        {/* Video */}
        <Reveal delay={200}>
          <div className="mb-10">
            <div className="w-full rounded-2xl overflow-hidden flex items-center justify-center cursor-pointer" style={{ aspectRatio: "16/9", background: "linear-gradient(135deg, #7C3AED, #6D28D9)" }}>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: "rgba(255,255,255,0.2)" }}>
                  <Play className="w-7 h-7 text-white ml-1" />
                </div>
                <p className="text-white font-semibold text-sm">How creators are using the full system to earn £1K–£5K/month</p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Comparison */}
        <Reveal delay={300}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
            {/* Toolkit */}
            <div className="bg-white rounded-xl p-6" style={{ border: "1px solid #E5E7EB" }}>
              <h3 className="font-bold text-base mb-4" style={{ color: "#1F2937" }}>Your Toolkit (£17)</h3>
              <div className="space-y-2.5">
                {toolkitFeatures.map((f, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: "#10B981" }} />
                    <span className="text-sm" style={{ color: "#374151" }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Full System */}
            <div className="rounded-xl p-6 relative" style={{ border: "2px solid #7C3AED", background: "#FAFAFA" }}>
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: "#7C3AED" }}>
                RECOMMENDED
              </div>
              <h3 className="font-bold text-base mb-4" style={{ color: "#1F2937" }}>AI StoryCraft Studio (Full System)</h3>
              <div className="space-y-2.5">
                {fullSystemFeatures.map((f, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: i < toolkitFeatures.length ? "#10B981" : "#7C3AED" }} />
                    <span className="text-sm" style={{ color: "#374151", fontWeight: i >= toolkitFeatures.length ? 600 : 400 }}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        {/* Pricing */}
        <Reveal delay={400}>
          <div className="text-center mb-8">
            <p className="text-sm mb-2" style={{ color: "#9CA3AF" }}>This one-time offer is only available on this page</p>
            <div className="flex items-center justify-center gap-3 mb-6">
              <span className="text-2xl line-through" style={{ color: "#9CA3AF" }}>£79</span>
              <span className="text-5xl font-extrabold" style={{ color: "#7C3AED" }}>£49</span>
            </div>

            <button onClick={handleUpgrade} disabled={purchasing} className="w-full md:w-auto font-bold text-lg px-12 py-4 rounded-full border-none text-white cursor-pointer transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] min-h-[56px]" style={{ backgroundColor: "#7C3AED", boxShadow: "0 4px 14px rgba(124,58,237,0.3)" }}>
              {purchasing ? "Processing..." : "Yes, Upgrade Me to the Full System → £49"}
            </button>

            <div className="mt-4">
              <Link to="/thank-you" className="text-sm no-underline transition-colors hover:underline" style={{ color: "#9CA3AF" }}>
                No thanks, I'll stick with just the toolkit →
              </Link>
            </div>
          </div>
        </Reveal>

        {/* Urgency */}
        <Reveal delay={450}>
          <p className="text-center text-xs mb-10" style={{ color: "#D97706" }}>⏰ This offer expires when you leave this page</p>
        </Reveal>

        {/* Testimonials */}
        <Reveal delay={500}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-xl p-5" style={{ border: "1px solid #E5E7EB" }}>
                <p className="text-sm italic mb-3 leading-relaxed" style={{ color: "#374151" }}>"{t.text}"</p>
                <p className="text-sm font-semibold" style={{ color: "#1F2937" }}>{t.name}</p>
                <p className="text-xs" style={{ color: "#9CA3AF" }}>{t.role}</p>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Guarantee */}
        <Reveal delay={600}>
          <div className="text-center mb-12">
            <Shield className="w-10 h-10 mx-auto mb-3" style={{ color: "#7C3AED" }} />
            <h3 className="font-bold text-lg mb-2" style={{ color: "#1F2937" }}>30-Day Money-Back Guarantee</h3>
            <p className="text-sm max-w-lg mx-auto" style={{ color: "#6B7280" }}>
              If the full system doesn't help you monetise within 30 days, get a full refund.
            </p>
          </div>
        </Reveal>

        {/* Footer */}
        <footer className="py-8" style={{ borderTop: "1px solid #E5E7EB" }}>
          <div className="flex items-center justify-center gap-4 text-xs" style={{ color: "#9CA3AF" }}>
            <Link to="/privacy-policy" className="hover:text-gray-600 transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-gray-600 transition-colors">Terms</Link>
          </div>
        </footer>
      </div>
    </div>
  );
}
